import {
  FunctionComponent,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { MapComponentWrapper } from '../globalStyles';
import {
  GoogleMap,
  InfoWindow,
  Polygon,
  useLoadScript
} from '@react-google-maps/api';
// import { PA_BOUNDARY } from '../../Constants/constants';
import { convertArrayToLatLng } from '../../CommonUtilities/CommonUtilities';
import { useReduxSelector } from '../../Store/reduxHooks';
import { RootState } from '../../store';
import { TransportationIcon } from '../../Universal/Assets';
import { STRINGS } from '../../Constants/ConstantStrings';
import dayjs from 'dayjs';
import { ClosuresData } from '../../Store/actions/Resources';
import PALoader from '../../SharedComponets/PALoader';
import GoogleMapsErrorBoundary from '../../GoogleMapsErrorBoundary';
import { GOOGLE_MAPS_API_KEY } from '../../settings';
import { PopupWindowContainer } from '../../SharedComponets/PopupWindow';
import AdvancedMarker from '../../SharedComponets/Map/AdvancedMarker';

const libraries: any = ['drawing', 'marker'];

const PopupWindowModal: FunctionComponent<any> = memo((props) => {
  const { closure, setSelectedClosure } = props;
  return (
    <PopupWindowContainer
      title={STRINGS.INCIDENT_DETAILS}
      onClose={() => setSelectedClosure(null)}
    >
      <h6>{closure?.wea_short_msg_english}</h6>
      <div className="row latitude-longitude-section">
        <div className="col-sm-6">
          <label>{STRINGS.LATITUDE}</label>
          <p>{closure?.centre_latitude}</p>
        </div>
        <div className="col-sm-6">
          <label>{STRINGS.LONGITUDE}</label>
          <p>{closure?.centre_longitude}</p>
        </div>
      </div>
      <div className="group mt-3">
        <label>{STRINGS.CURRENT_CONDITION}</label>
        <p>{closure?.description}</p>
      </div>

      <div className="group">
        <label>{STRINGS.UPDATED_ON}</label>
        <p> {dayjs(closure?.created_on).format('MM/DD/YYYY  hh:mm A')}</p>
      </div>
    </PopupWindowContainer>
  );
});

interface ResourcesMapPropType {
  selectedClosure: any;
  setSelectedClosure: any;
}

const ResourcesMap: FunctionComponent<ResourcesMapPropType> = (props) => {
  const { selectedClosure, setSelectedClosure } = props;
  const closuresData = useReduxSelector(
      (state: RootState) => state.Resources.mapData
    ),
    isFetchingMapData = useReduxSelector(
      (state: RootState) => state.Resources.isFetchingMapData
    );

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: libraries
  });
  const [center] = useState<{ lat: any; lng: any }>({
      lat: 40.881086,
      lng: -77.692104
    }),
    mapRef = useRef<google.maps.Map | null>(null);

  useEffect(() => {
    if (closuresData?.length) {
      const tempBounds = new google.maps.LatLngBounds();
      closuresData.forEach((closure: ClosuresData) => {
        closure.geometry?.coordinates?.[0]?.forEach((coord: any) => {
          tempBounds.extend({
            lat: coord[0][1],
            lng: coord[0][0]
          });
        });
      });
      mapRef.current?.fitBounds(tempBounds);
    }
  }, [closuresData]);

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
    },
    [mapRef]
  );

  return !isFetchingMapData && isLoaded ? (
    <MapComponentWrapper id="map-wrapper">
      <Suspense fallback={<PALoader />}>
        <GoogleMapsErrorBoundary>
          <GoogleMap
            onLoad={onMapLoad}
            center={center}
            mapContainerStyle={{
              width: '100%',
              height: '100%',
              borderRadius: 8
            }}
            zoom={8}
            options={{
              mapId: 'pa-connect-map',
              mapTypeControl: true,
              mapTypeControlOptions: {
                position: google.maps.ControlPosition.BOTTOM_LEFT, // Set position of the control
                mapTypeIds: ['roadmap', 'satellite', 'terrain', 'hybrid']
              },
              fullscreenControl: true,
              fullscreenControlOptions: {
                position: google.maps.ControlPosition.BOTTOM_RIGHT
              }
              // restriction: {
              //   strictBounds: false,
              //   latLngBounds: PA_BOUNDARY
              // }
            }}
          >
            {closuresData?.map((closure) => {
              return (
                <>
                  {closure.geometry?.coordinates?.[0].map((coord, index) => (
                    <Polygon
                      key={index}
                      paths={convertArrayToLatLng(coord)}
                      options={{
                        fillColor: 'red',
                        fillOpacity: 0.2,
                        strokeWeight: 3,
                        strokeColor: 'red',
                        clickable: false
                      }}
                    />
                  ))}
                </>
              );
            })}

            {closuresData?.map((closure, index) => (
              <AdvancedMarker
                key={index}
                position={{
                  lat: closure.centre_latitude,
                  lng: closure.centre_longitude
                }}
                onClick={() => setSelectedClosure(index)}
              >
                <img
                  src={TransportationIcon}
                  alt="transportation"
                  draggable={false}
                />

                {selectedClosure === index && (
                  <InfoWindow
                    onCloseClick={() => setSelectedClosure(null)}
                    position={{
                      lat: closure.centre_latitude,
                      lng: closure.centre_longitude
                    }}
                  >
                    <PopupWindowModal
                      closure={closure}
                      setSelectedClosure={setSelectedClosure}
                    />
                  </InfoWindow>
                )}
              </AdvancedMarker>
            ))}
          </GoogleMap>
        </GoogleMapsErrorBoundary>
      </Suspense>
    </MapComponentWrapper>
  ) : (
    <PALoader />
  );
};

export default ResourcesMap;
